import type { HarborStationProps, HarborSummary, LayerState } from '@vitals/shared';
import type { VitalDef, VitalTone } from './vitals.js';

/** Wave height threshold for a warn tone (> 6 ft significant waves). */
const WAVE_WARN_FT = 6;

function stationDetails(features: LayerState['features']) {
  return features.flatMap((feature) => {
    const props = feature.props as Partial<HarborStationProps>;
    if (typeof props.name !== 'string' || props.name.trim() === '') return [];

    const readings: string[] = [];
    if (typeof props.waterTempF === 'number' && Number.isFinite(props.waterTempF)) {
      readings.push(`Water ${props.waterTempF}°F`);
    }
    if (typeof props.waveHeightFt === 'number' && Number.isFinite(props.waveHeightFt)) {
      readings.push(`Waves ${props.waveHeightFt} ft`);
    }
    if (typeof props.windMph === 'number' && Number.isFinite(props.windMph)) {
      const direction = typeof props.windDir === 'string' && props.windDir.trim() !== ''
        ? ` ${props.windDir}`
        : '';
      readings.push(`Wind ${Math.round(props.windMph)} mph${direction}`);
    }
    if (typeof props.airTempF === 'number' && Number.isFinite(props.airTempF)) {
      readings.push(`Air ${props.airTempF}°F`);
    }

    return [{
      kind: 'alert' as const,
      title: props.name,
      ...(readings.length > 0 && { meta: readings.join(' · ') }),
    }];
  });
}

/**
 * Vital selector for the NOAA NDBC harbor buoy (station 44065, NY Harbor Entrance).
 *
 * Display: value = water temp (°F), label "harbor".
 * tone: warn when significant wave height > 6 ft.
 * Down/no-summary: value "—".
 */
export const harborVital: VitalDef = {
  id: 'harbor',
  layerId: 'harbor',

  select(s: LayerState | undefined) {
    const isDown = !s || s.status === 'down' || s.summary == null;
    if (isDown) {
      return {
        value: '—',
        label: 'harbor',
        down: true,
        stale: false,
      };
    }

    const stale = s.status === 'stale';
    const summary = s.summary as HarborSummary;
    const { waterTempF, waveHeightFt } = summary;

    const tone: VitalTone | undefined =
      waveHeightFt !== undefined && waveHeightFt > WAVE_WARN_FT ? 'warn' : undefined;
    const details = stationDetails(s.features);

    return {
      value: `${waterTempF}°F`,
      label: 'harbor',
      down: false,
      stale,
      ...(tone !== undefined && { tone }),
      ...(details.length > 0 && { detailTitle: 'Harbor buoys', details }),
    };
  },
};
